/**
 * DialogProvider - Global dialog state management
 *
 * Provides context for managing feature dialogs, permission dialogs, and general error dialogs
 * throughout the application. Renders dialogs at the provider level to avoid prop drilling.
 *
 * @example
 * // In any component
 * const { showFeatureDialog, showPermissionDialog, showGeneralError } = useDialogContext();
 * showFeatureDialog(PartnerFeatureEnum.BulkMessaging);
 * showPermissionDialog();
 * showGeneralError();
 */

import React, { createContext, useContext, useState, ReactNode } from "react";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { FeatureDialog } from "@/components/FeatureDialog";
import { GeneralErrorAlertDialog } from "@/components/GeneralErrorAlertDialog";

interface DialogContextType {
  showFeatureDialog: (feature: PartnerFeatureEnum) => void;
  showPermissionDialog: () => void;
  showGeneralError: () => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialogContext = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialogContext must be used within a DialogProvider");
  }
  return context;
};

interface DialogProviderProps {
  children: ReactNode;
}

export const DialogProvider = ({ children }: DialogProviderProps) => {
  const [featureDialog, setFeatureDialog] = useState<{
    isOpen: boolean;
    feature: PartnerFeatureEnum | null;
    type: "feature" | "permission";
  }>({
    isOpen: false,
    feature: null,
    type: "feature",
  });

  const [generalError, setGeneralError] = useState(false);

  const showFeatureDialog = (feature: PartnerFeatureEnum) => {
    setFeatureDialog({
      isOpen: true,
      feature,
      type: "feature",
    });
  };

  const showPermissionDialog = () => {
    setFeatureDialog({
      isOpen: true,
      feature: null,
      type: "permission",
    });
  };

  const showGeneralError = () => {
    setGeneralError(true);
  };

  const handleFeatureDialogClose = (open: boolean) => {
    if (!open) {
      setFeatureDialog({
        isOpen: false,
        feature: null,
        type: "feature",
      });
    }
  };

  const handleGeneralErrorClose = (open: boolean) => {
    setGeneralError(open);
  };

  return (
    <DialogContext.Provider
      value={{ showFeatureDialog, showPermissionDialog, showGeneralError }}
    >
      {children}

      {/* Global dialogs rendered here */}
      <FeatureDialog
        isOpen={featureDialog.isOpen}
        onOpenChange={handleFeatureDialogClose}
        type={featureDialog.type}
        feature={featureDialog.feature}
      />

      <GeneralErrorAlertDialog
        isOpen={generalError}
        onOpenChange={handleGeneralErrorClose}
      />
    </DialogContext.Provider>
  );
};
